import React from 'react';
import {
  Beaker,
  CheckList,
  Clipboard,
  FileText,
  Helix,
  Home,
  Activity,
  Layers,
  PlayCircle,
  Procedure,
  SparkleCircle,
  SparkleFile,
  SparkleHeart,
  Tablet,
} from '@lifeomic/chromicons';
import { memberDetailMessages } from './messages';
import { MemberSection } from './types';
import urls from '../../../../util/urls';

type MemberDetailSectionRequirement =
  | 'fountainLifePowerUser'
  | 'regenerativeMedicineOps'
  | 'genomics'
  | 'zoriPulsePriorities'
  | 'genericCompositeReport';

export type MemberDetailSectionConfig = {
  section: MemberSection;
  labelMessage: keyof typeof memberDetailMessages;
  menuMessage: keyof typeof memberDetailMessages;
  descriptionMessage?: keyof typeof memberDetailMessages;
  icon: React.ComponentType<{ className?: string }>;
  requirement?: MemberDetailSectionRequirement;
  showOnLanding: boolean;
  quickAccess?: boolean;
};

export type MemberDetailSectionVisibility = {
  fountainLifePowerUserEnabled: boolean;
  regenerativeMedicineOpsEnabled: boolean;
  includeGenomics?: boolean;
  zoriPulsePrioritiesEnabled?: boolean;
  genericCompositeReportEnabled?: boolean;
};

export const MEMBER_DETAIL_SECTION_CONFIGS: MemberDetailSectionConfig[] = [
  {
    section: 'landing',
    labelMessage: 'landingLabel',
    menuMessage: 'landingTitle',
    icon: Home,
    showOnLanding: false,
  },
  {
    section: 'onboarding-snapshot',
    labelMessage: 'onboardingSnapshotLabel',
    menuMessage: 'onboardingSnapshot',
    descriptionMessage: 'landingOnboardingSnapshotDescription',
    icon: PlayCircle,
    showOnLanding: true,
  },
  {
    section: 'pulse',
    labelMessage: 'pulseLabel',
    menuMessage: 'pulseMenu',
    descriptionMessage: 'landingPulseDescription',
    icon: Activity,
    requirement: 'zoriPulsePriorities',
    showOnLanding: true,
  },
  {
    section: 'regenerative-medicine-ops',
    labelMessage: 'regenerativeMedicineOpsLabel',
    menuMessage: 'regenerativeMedicineOps',
    descriptionMessage: 'landingRegenerativeMedicineOpsDescription',
    icon: SparkleHeart,
    requirement: 'regenerativeMedicineOps',
    showOnLanding: true,
  },
  {
    section: 'annual-report',
    labelMessage: 'annualReportLabel',
    menuMessage: 'annualReport',
    descriptionMessage: 'landingAnnualReportDescription',
    icon: FileText,
    showOnLanding: true,
    quickAccess: true,
  },
  {
    section: 'composite-report',
    labelMessage: 'compositeReportLabel',
    menuMessage: 'compositeReport',
    descriptionMessage: 'landingCompositeReportDescription',
    icon: Layers,
    requirement: 'genericCompositeReport',
    // Also shown as a card under "Other tools" on the landing page (CR-9/CR-10
    // UI gap fix) — this is in addition to, not instead of, the dedicated
    // sidebar tab above.
    showOnLanding: true,
  },
  {
    section: 'action-plan',
    labelMessage: 'actionPlanLabel',
    menuMessage: 'actionPlan',
    descriptionMessage: 'landingActionPlanDescription',
    icon: CheckList,
    showOnLanding: true,
  },
  {
    section: 'ai-tools',
    labelMessage: 'aiToolsLabel',
    menuMessage: 'aiTools',
    descriptionMessage: 'landingAiToolsDescription',
    icon: SparkleFile,
    showOnLanding: true,
    quickAccess: true,
  },
  {
    section: 'rule-action-audit',
    labelMessage: 'ruleActionAuditLabel',
    menuMessage: 'ruleActionAudit',
    descriptionMessage: 'landingRuleActionAuditDescription',
    icon: Procedure,
    requirement: 'fountainLifePowerUser',
    showOnLanding: true,
  },
  {
    section: 'labs',
    labelMessage: 'labsLabel',
    menuMessage: 'labsAndData',
    descriptionMessage: 'landingLabsDescription',
    icon: Beaker,
    showOnLanding: true,
    quickAccess: true,
  },
  {
    section: 'documents',
    labelMessage: 'documentsLabel',
    menuMessage: 'documents',
    descriptionMessage: 'landingDocumentsDescription',
    icon: Clipboard,
    showOnLanding: true,
  },
  {
    section: 'surveys',
    labelMessage: 'surveysLabel',
    menuMessage: 'surveys',
    descriptionMessage: 'landingSurveysDescription',
    icon: CheckList,
    showOnLanding: true,
  },
  {
    section: 'member-app',
    labelMessage: 'memberAppLabel',
    menuMessage: 'memberAppLabel',
    descriptionMessage: 'landingMemberAppDescription',
    icon: Tablet,
    showOnLanding: true,
  },
  {
    section: 'omics-explorer',
    labelMessage: 'omicsExplorerLabel',
    menuMessage: 'omicsExplorer',
    descriptionMessage: 'landingOmicsExplorerDescription',
    icon: Helix,
    requirement: 'genomics',
    showOnLanding: true,
  },
];

export const ZORI_CHAT_TILE_CONFIG = {
  labelMessage: 'zoriChatLabel',
  descriptionMessage: 'landingZoriChatDescription',
  icon: SparkleCircle,
  quickAccess: true,
} as const;

export const getVisibleMemberDetailSections = ({
  fountainLifePowerUserEnabled,
  regenerativeMedicineOpsEnabled,
  includeGenomics = true,
  zoriPulsePrioritiesEnabled = false,
  genericCompositeReportEnabled = false,
}: MemberDetailSectionVisibility) =>
  MEMBER_DETAIL_SECTION_CONFIGS.filter(config => {
    if (config.requirement === 'fountainLifePowerUser') {
      return fountainLifePowerUserEnabled;
    }
    if (config.requirement === 'regenerativeMedicineOps') {
      return regenerativeMedicineOpsEnabled;
    }
    if (config.requirement === 'genomics') {
      return includeGenomics;
    }
    if (config.requirement === 'zoriPulsePriorities') {
      return zoriPulsePrioritiesEnabled;
    }
    if (config.requirement === 'genericCompositeReport') {
      return genericCompositeReportEnabled;
    }
    return true;
  });

export const getMemberDetailSectionConfig = (section: MemberSection) =>
  MEMBER_DETAIL_SECTION_CONFIGS.find(config => config.section === section);

export const getMemberDetailSectionPath = (
  memberId: string,
  section: MemberSection,
) =>
  section === 'landing'
    ? urls.app.members(memberId)
    : `${urls.app.members(memberId)}/${section}`;
